'use client';

import { useEffect, useState } from 'react';

import { athenaPost } from './athena-fetch';
import { useUserSession } from './user-session';

import { isRecord, stringField } from '@/lib/athena-utils';

const BUYER_OFFERS_MINIMAL_QUERY = /* GraphQL */ `
  query BuyerOffersMinimal {
    customerAccount {
      buying {
        offers {
          id
          transactionState
        }
      }
    }
  }
`;

// Mirrors cosmos useHasActiveBuyerOffers: any offer not in one of these
// transaction states counts as active.
const INACTIVE_STATES = new Set([
  'CANCELED',
  'BUYER_WALKED',
  'REJECTED',
  'CONTRACT_FELL_THROUGH',
  'BACKUP_EXPIRED',
  'CLOSED',
  'UNKNOWN',
]);

function extractHasActive(data: unknown): boolean {
  if (!isRecord(data)) return false;
  const customerAccount = data['customerAccount'];
  if (!isRecord(customerAccount)) return false;
  const buying = customerAccount['buying'];
  if (!isRecord(buying)) return false;
  const offers = buying['offers'];
  if (!Array.isArray(offers)) return false;
  for (const offer of offers) {
    if (!isRecord(offer)) continue;
    const state = stringField(offer, 'transactionState');
    if (!state) {
      // No state mapped → treat as active, matching cosmos behavior where
      // unknown transaction states are surfaced rather than hidden.
      return true;
    }
    if (!INACTIVE_STATES.has(state)) return true;
  }
  return false;
}

// Keyed by customerUuid because BuyerOffersMinimal's identity rides on the
// session cookie rather than a query variable; the uuid is the stable
// per-customer cache key, not a query argument.
const cache = new Map<string, boolean>();
const inflight = new Map<string, Promise<boolean>>();

async function fetchHasActiveOffers(cacheKey: string): Promise<boolean> {
  const data = await athenaPost<unknown>({
    operationName: 'BuyerOffersMinimal',
    query: BUYER_OFFERS_MINIMAL_QUERY,
    variables: {},
  });
  if (data === undefined) return false;
  const result = extractHasActive(data);
  cache.set(cacheKey, result);
  return result;
}

export function useHasActiveBuyerOffers(): boolean {
  const auth = useUserSession();
  const customerUuid = auth.kind === 'authenticated' ? auth.human.customerUuid : undefined;
  const [hasActive, setHasActive] = useState<boolean>(() =>
    customerUuid ? (cache.get(customerUuid) ?? false) : false,
  );

  useEffect(() => {
    if (!customerUuid) {
      setHasActive(false);
      return;
    }
    const cached = cache.get(customerUuid);
    if (cached !== undefined) {
      setHasActive(cached);
      return;
    }
    let cancelled = false;
    let request = inflight.get(customerUuid);
    if (!request) {
      request = fetchHasActiveOffers(customerUuid).then((result) => {
        inflight.delete(customerUuid);
        return result;
      });
      inflight.set(customerUuid, request);
    }
    request.then((result) => {
      if (!cancelled) setHasActive(result);
    });
    return () => {
      cancelled = true;
    };
  }, [customerUuid]);

  return hasActive;
}
